import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { SentenceWithPost } from "./types";
import { blueskyPostUrl } from "../../lib/bluesky";

interface HoverPos {
  x: number;
  y: number;
  side: "right" | "left" | "below";
}

/** Width must exceed height by at least this much to place the preview
 *  beside the lifted card; otherwise it goes below to avoid overflow on
 *  portrait/square viewports. */
const SIDE_PLACEMENT_MIN_LANDSCAPE_DELTA = 100;

export function SentenceRow({ sentence }: { sentence: SentenceWithPost }) {
  // Prefer the full at-URI (source_uri) when present; fall back to the
  // bare rkey in source_ref. Either parses through blueskyPostUrl.
  const url = blueskyPostUrl(
    sentence.author_did,
    sentence.source_uri ?? sentence.source_ref,
  );
  const [hoverPos, setHoverPos] = useState<HoverPos | null>(null);
  const anchorRef = useRef<HTMLAnchorElement | null>(null);

  const Inner = (
    <div className="border-rule/60 flex items-baseline gap-3 border-b py-2 last:border-b-0">
      <span
        className="text-ink flex-1 text-[13px] leading-snug"
        style={{ fontFamily: "var(--font-jp-body), serif" }}
      >
        {sentence.sentence_text}
      </span>
      {url ? (
        <span className="n5-marginalia shrink-0" aria-hidden>
          →
        </span>
      ) : null}
    </div>
  );

  if (!url) return Inner;

  const handleEnter = () => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const previewW = 320;
    const margin = 14;

    const isLandscape =
      window.innerWidth - window.innerHeight >=
      SIDE_PLACEMENT_MIN_LANDSCAPE_DELTA;
    if (!isLandscape) {
      const dialog = el.closest('[role="dialog"]') as HTMLElement | null;
      const dr = dialog?.getBoundingClientRect();
      if (dr) {
        const x = Math.max(
          margin,
          Math.min(
            window.innerWidth - previewW - margin,
            dr.left + (dr.width - previewW) / 2,
          ),
        );
        setHoverPos({ x, y: dr.bottom + margin, side: "below" });
        return;
      }
    }

    const fitsRight = r.right + margin + previewW < window.innerWidth;
    setHoverPos({
      x: fitsRight ? r.right + margin : r.left - margin - previewW,
      y: r.top,
      side: fitsRight ? "right" : "left",
    });
  };
  const handleLeave = () => setHoverPos(null);

  return (
    <>
      <a
        ref={anchorRef}
        href={url}
        target="_blank"
        rel="noreferrer noopener"
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onFocus={handleEnter}
        onBlur={handleLeave}
        className="hover:bg-paper-tint -mx-3 block px-3 transition-colors"
      >
        {Inner}
      </a>
      {hoverPos && typeof window !== "undefined"
        ? createPortal(
            <PostPreview sentence={sentence} url={url} pos={hoverPos} />,
            document.body,
          )
        : null}
    </>
  );
}

function PostPreview({
  sentence,
  url,
  pos,
}: {
  sentence: SentenceWithPost;
  url: string;
  pos: HoverPos;
}) {
  return (
    <div
      className="z-50"
      role="tooltip"
      style={{
        position: "fixed",
        left: pos.x,
        top: pos.y,
        width: 320,
        pointerEvents: "none",
      }}
    >
      <a
        href={url}
        target="_blank"
        rel="noreferrer noopener"
        className="block"
        style={{
          background: "var(--paper)",
          border: "2px solid var(--ink)",
          padding: "12px 14px",
          pointerEvents: "auto",
          boxShadow:
            "6px 6px 0 -1px var(--rule), 0 16px 40px -10px color-mix(in oklch, var(--ink) 45%, transparent)",
        }}
      >
        <div className="n5-marginalia mb-2" style={{ letterSpacing: "0.1em" }}>
          bluesky · post
        </div>
        <p
          className="text-ink text-[13px] leading-relaxed whitespace-pre-wrap"
          style={{ fontFamily: "var(--font-jp-body), serif" }}
        >
          {highlightSentence(sentence.post_text, sentence.sentence_text)}
        </p>
        <div
          className="n5-marginalia mt-3 flex items-center justify-end gap-2 pt-2"
          style={{
            letterSpacing: "0.06em",
            borderTop: "1px solid var(--rule)",
          }}
        >
          <span>click to open in bluesky</span>
          <span aria-hidden>→</span>
        </div>
      </a>
    </div>
  );
}

/**
 * Render the post text with the matched sentence highlighted so the
 * reader can see the lemma's home in context. If the sentence isn't
 * found verbatim, fall back to plain text.
 */
function highlightSentence(post: string, sentence: string): React.ReactNode {
  const idx = post.indexOf(sentence);
  if (idx === -1) return post;
  return (
    <>
      {post.slice(0, idx)}
      <span
        style={{
          background: "color-mix(in oklch, var(--claimed) 18%, transparent)",
        }}
      >
        {sentence}
      </span>
      {post.slice(idx + sentence.length)}
    </>
  );
}
